import { reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';
import jwt from 'jsonwebtoken';

import { AccessMemberInfoType, EditPasswordType, MemberInfoType, SignupType } from '@/interface/member';

const useMemberStore = defineStore('memberStore', () => {
  const confirmPassword = ref<string>(''); // 비밀번호 확인 입력값
  const confirmEditPassword = ref<string>(''); // 비밀번호 변경 확인 입력값
  const emailRegex = /[a-z0-9]{2,}@[a-z0-9-]{2,}\.[a-z0-9]{2,}/i; // 이메일 유효성 검사 정규식

  // 이메일, 비밀번호 확인 에러 메세지
  const errorMessage = reactive({
    email: '',
    confirmPassword: '',
    loginFailed: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  // 회원가입 폼 유저 정보
  const signupForm: SignupType = reactive({
    name: '',
    email: '',
    nickname: '',
    password: '',
  });

  // 비밀번호 변경 입력 폼
  const editPasswordForm: EditPasswordType = reactive({
    current: '',
    renew: '',
  });

  // 닉네임 변경 입력 폼
  const editNickname = ref<string>('');

  // 로그인 된 유저 정보
  const accessedMemberInfo: MemberInfoType = reactive({
    id: 0,
    email: '',
    name: '',
    nickname: '',
    phone: '',
    level: '',
    award: '',
  });

  /**
   * 회원가입
   */
  const accessSignup = async (): Promise<MemberInfoType | undefined> => {
    const email = signupForm.email.trim();

    let signupResponse;

    if (emailRegex.test(email)) {
      await axios.post<string>('/api/members', signupForm)
        .then(({ data }): void => {
          if (!data) return;
          Object.assign(data, {
            name: '',
            email: '',
            nickname: '',
            password: '',
          });
          signupResponse = data;
        })
        .catch((e) => {
          errorMessage.email = e.response.data.message;
        });
      return signupResponse;
    }
  };

  /**
   * 유저 로그인 (accessToken 값 불러오기)
   * @param loginFormData
   * @param isRememberChecked
   */
  const accessLogin = async (loginFormData: AccessMemberInfoType, isRememberChecked: boolean): Promise<void> => {
    const email = loginFormData.email.trim();

    let loginResponse;

    if (emailRegex.test(email)) {
      await axios.post<string>('/api/login', loginFormData)
        .then(({ data }): void => {
          if (!data) return;
          if (isRememberChecked) {
            localStorage.setItem('accessToken', data);
          } else {
            sessionStorage.setItem('accessToken', data);
          }
          loginResponse = data;
        })
        .catch((e) => {
          errorMessage.loginFailed = e.response.data.message;
        });

      return loginResponse;
    }
  };

  /**
   * 회원 로그아웃 (accessToken 값 삭제하기)
   */
  const accessLogout = (): void => {
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('accessToken');
    Object.assign(accessedMemberInfo, {
      id: 0,
      email: '',
      name: '',
      nickname: '',
      phone: '',
      level: '',
      award: '',
    });
  };

  /**
   * 유저 닉네임 변경
   * @param newNickname
   * @param token
   */
  const accessEditNickname = async (newNickname: string, token: string | null): Promise<MemberInfoType | undefined> => {
    const decodeToken = jwt.decode(token);

    let nicknameResponse;

    const configHeaders = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    };

    if (token) {
      await axios.patch<string>(`/api/members/${decodeToken.id}/nickname`, { 'renew': newNickname }, configHeaders)
        .then(({ data }): void => {
          if (!data) return;
          nicknameResponse = data;
        })
        .catch((e) => {
          console.log(e);
        });

      return nicknameResponse;
    }
  };

  /**
   * 비밀번호 변경
   * @param data
   * @param token
   */
  const accessEditPassword = async (data: EditPasswordType, token: string | null): Promise<MemberInfoType | undefined> => {
    const decodeToken = jwt.decode(token);

    let editPasswordResponse;

    const configHeaders = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    };

    if (token) {
      await axios.patch<string>(`/api/members/${decodeToken.id}/password`, data, configHeaders)
        .then(({ data }): void => {
          if (!data) return;
          editPasswordResponse = data;
        })
        .catch((e) => {
          errorMessage.currentPassword = e.response.data.message;
        });

      return editPasswordResponse;
    }
  };

  /**
   * 등급명 영문 대문자로 변환해서 가져옵니다.
   */
  const getLevelName = (): string => {
    switch (accessedMemberInfo.level) {
      case '데브라':
        return 'DEBRA';
      case '메테':
        return 'METE';
      case '코롱이':
        return 'KORONG';
      case '관리자':
        return 'ADMIN';
      default:
        return 'GUEST';
    }
  };

  /**
   * 등급에 따라 다른 이미지를 불러옵니다.
   */
  const getLevelImageSrc = (level: string | null, position: string): string => {
    let changeLevelLang: string = level ? level : '';

    switch (changeLevelLang) {
      case '데브라':
        changeLevelLang = 'Debra';
        break;
      case '메테':
        changeLevelLang = 'Mete';
        break;
      case '코롱이':
        changeLevelLang = 'Korong';
        break;
    }

    switch (changeLevelLang) {
      case 'Debra':
      case 'Mete':
      case 'Korong':
        return require(`@/assets/images/${changeLevelLang}/${changeLevelLang}${position}.png`);
      default:
        return require(`@/assets/images/ContecCiBlue${position}.png`);
    }
  };


  return {
    confirmPassword,
    confirmEditPassword,
    emailRegex,
    errorMessage,
    signupForm,
    editPasswordForm,
    editNickname,
    accessedMemberInfo,
    accessSignup,
    accessLogin,
    accessLogout,
    accessEditNickname,
    accessEditPassword,
    getLevelName,
    getLevelImageSrc,
  };
});

export default useMemberStore;